import { Fragment, useState } from "react";
import AcceptReturnItemButtonComponent from "../ModalComponent/AcceptReturnItemButtonComponent";
import InitiateReturnProcess from "../ModalComponent/InitiateReturnProcess";
import PackButtonComponent from "../ModalComponent/PackButtonComponent";
import QCActionModal from "../ModalComponent/QcActionModal/QcActionModal";
import { useOrderCancelOnPartner } from "@/app/hooks/api_hooks/orders/useOrderCancelOnPartner";
import { useOrderForceStopDeliveryAPI } from "@/app/hooks/api_hooks/orders/useOrderForceStopDeliveryAPI";
import { useOrderManagementOrderListAPI } from "@/app/hooks/api_hooks/orders/useOrderListAPI";
import { useOrderManagementStatsAPI } from "@/app/hooks/api_hooks/orders/useOrderStatsAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IOrderManagementState } from "@/app_redux/reducers/slices/auth/order_management_slice";
import { IOrderManagementFilterBody, IOrderManagementStatsFilterBody } from "@/api_framework/api_modals/Orders";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { FiMoreVertical } from "react-icons/fi";
import { useCreateRxMedicinePrescriptionAPI } from "@/app/hooks/api_hooks/orders/useCreateRxMedicinePrescriptionAPI";
import { useCentralOrderPaymentLinkAPI } from "@/app/hooks/api_hooks/orders/useCentralOrderPaymentLinkAPI";
import { IRxMedicinePrescriptionCreateData } from "@/api_framework/api_modals/EMRs";
import RxEMRPreviewModal from "../ModalComponent/RxEMRPreviewModal";
import HDCartCheckoutModal from "../../HDCart/CheckoutModal";
import toast from "react-hot-toast";
import RBACFlag from "@/app/components/core/RBACFlag";

const OrderActionButton = ({ orders, module }) => {
  const [open, setOpen] = useState(false);
  const [acceptReturnItem, setAcceptReturnItem] = useState(false);
  const [isCancelRiderModalOpen, setIsCancelRiderModalOpen] = useState(false);
  const [isCancelRiderInProgress, setIsCancelRiderInProgress] = useState(false);
  const [menuQcModalAction, setMenuQcModalAction] = useState<string | null>(null);
  const [isMenuInitiateReturnOpen, setIsMenuInitiateReturnOpen] = useState(false);
  const [isStopAssignmentModalOpen, setIsStopAssignmentModalOpen] = useState(false);
  const [isStopAssignmentInProgress, setIsStopAssignmentInProgress] = useState(false);
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState } = useRouteInfo(routeId);
  const { orderFilter, orderModule } = routeState.state as IOrderManagementState;
  const cancelPartnerOrders = useOrderCancelOnPartner();
  const forceStopDelivery = useOrderForceStopDeliveryAPI();
  const fetchOrderListAPI = useOrderManagementOrderListAPI();
  const fetchStatsAPI = useOrderManagementStatsAPI();
  const [copied, setCopied] = useState(false);
  const [isGenerateEmrInProgress, setIsGenerateEmrInProgress] = useState(false);
  const [isRxEmrPreviewVisible, setIsRxEmrPreviewVisible] = useState(false);
  const [rxEmrPreviewData, setRxEmrPreviewData] = useState<IRxMedicinePrescriptionCreateData | null>(null);
  const createRxMedicinePrescriptionAPI = useCreateRxMedicinePrescriptionAPI();
  const { execute: fetchCentralOrderPaymentLink } = useCentralOrderPaymentLinkAPI();
  const [isFetchingPaymentLink, setIsFetchingPaymentLink] = useState(false);
  const [isSendPaymentLinkModalOpen, setIsSendPaymentLinkModalOpen] = useState(false);
  const [sendPaymentLinkOrderPlacedOpen, setSendPaymentLinkOrderPlacedOpen] = useState(false);

  const refreshOrderListAndStats = async () => {
    const currentOrderStatus = orders?.order_details?.order_status?.toLowerCase?.();
    const refreshOrderStatus = currentOrderStatus === "rx_pending" ? "rx_pending" : "";

    const statsBody: IOrderManagementStatsFilterBody = {
      start_date: orderFilter?.start_date,
      end_date: orderFilter?.end_date,
      clinic_id: orderFilter?.clinic_id?.split(","),
    };
    const requestBody: IOrderManagementFilterBody = {
      clinic_id: orderFilter?.clinic_id?.split(","),
      start_date: orderFilter?.start_date,
      end_date: orderFilter?.end_date,
      order_status: refreshOrderStatus,
      source: orderModule,
    };

    await Promise.all([fetchOrderListAPI(requestBody), fetchStatsAPI(statsBody)]);
  };

  const handelCancelPartnerorder = async () => {
    await cancelPartnerOrders(orders?.order_details?.sub_order_id);
    await refreshOrderListAndStats();
  };

  const handleConfirmCancelRiderRequest = async () => {
    if (isCancelRiderInProgress) return;
    setIsCancelRiderInProgress(true);
    try {
      await handelCancelPartnerorder();
      setIsCancelRiderModalOpen(false);
    } finally {
      setIsCancelRiderInProgress(false);
    }
  };

  const handleConfirmStopAssignment = async () => {
    if (isStopAssignmentInProgress) return;
    setIsStopAssignmentInProgress(true);
    try {
      const status = await forceStopDelivery(orders?.order_details?.sub_order_id);
      if (status) {
        await refreshOrderListAndStats();
        setIsStopAssignmentModalOpen(false);
      }
    } finally {
      setIsStopAssignmentInProgress(false);
    }
  };

  const handleCopyPaymentLink = async () => {
    const orderId = orders?.order_details?.order_id;
    if (!orderId || isFetchingPaymentLink) return;

    setIsFetchingPaymentLink(true);
    try {
      const response = await fetchCentralOrderPaymentLink(orderId);
      if (response?.status && response?.data?.status?.toLowerCase() === "ready") {
        if (response?.message) {
          toast.success(response.message);
        }
        if (response?.data?.payment_link_url) {
          await navigator.clipboard.writeText(response.data.payment_link_url);
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 1500);
        }
      } else if (response?.message) {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Fetch & copy payment link failed:", error);
    } finally {
      setIsFetchingPaymentLink(false);
    }
  };

  const handleSendPaymentLink = () => {
    setSendPaymentLinkOrderPlacedOpen(false);
    setIsSendPaymentLinkModalOpen(true);
  };

  const createRxEMR = async (orderId: string) => {
    if (isGenerateEmrInProgress) return;

    setIsGenerateEmrInProgress(true);
    try {
      const response = await createRxMedicinePrescriptionAPI({
        order_id: orderId,
      });

      if (response?.status) {
        setRxEmrPreviewData((response?.data as IRxMedicinePrescriptionCreateData) ?? null);
        setIsRxEmrPreviewVisible(true);
      }
    } finally {
      setIsGenerateEmrInProgress(false);
    }
  };

  const handleGenerateEmr = async () => {
    const orderId = orders?.order_details?.order_id;

    if (!orderId) {
      return;
    }

    await createRxEMR(orderId);
  };

  const primaryActionBtnClass =
    "w-full rounded-lg bg-black px-1 py-2 text-xs font-medium text-white transition-opacity hover:opacity-90";
  const menuActionBtnClass = (active: boolean) =>
    `w-full text-left px-4 py-2 text-sm transition-colors ${active ? "bg-gray-50 text-black" : "text-gray-700"}`;

  const status = orders?.order_details?.order_status?.toLowerCase();
  const centralActions = [];
  const clinicActions = [];

  if (["ready_for_pickup", "undelivered", "cancelled", "picked"]?.includes(status)) {
    centralActions.push({ key: "manual", kind: "qc_modal", label: "Manually Manage", buttonId: "MANUALY" });
    if (status !== "cancelled") {
      centralActions.push({ key: "cancel", kind: "qc_modal", label: "Cancel Order", buttonId: "CANCEL" });
    }
    if (["ready_for_pickup", "confirmed"]?.includes(status)) {
      centralActions.push({ key: "cancel-rider", kind: "cancel_rider", label: "Cancel Rider Request" });
    }
  }

  if (status === "confirmed") {
    centralActions.push({ key: "cancel-confirmed", kind: "qc_modal", label: "Cancel Order", buttonId: "CANCEL" });
    centralActions.push({ key: "cancel-rider-confirmed", kind: "cancel_rider", label: "Cancel Rider Request" });
  }

  if (status === "undelivered") {
    centralActions.push({ key: "reattempt", kind: "qc_modal", label: "Reattempt", buttonId: "REATTEMPT" });
  }

  if (["delivered", "picked", "delivered_offline"]?.includes(status)) {
    centralActions.push({ key: "initiate-return", kind: "initiate_return", label: "Initiate Return" });
  }

  if (["payment_due"]?.includes(status) && orders?.source === "portal") {
    centralActions.push({
      key: "cancel-payment-due",
      kind: "qc_modal",
      label: "Cancel Order",
      buttonId: "CANCEL_ORDER",
    });
  }

  const prescriptionDoneValue = String(orders?.prescription_status ?? "")
    .trim()
    .toLowerCase();

  if (status === "rx_pending" && prescriptionDoneValue === "required") {
    centralActions.push({ key: "generate-emr-central", kind: "generate_emr", label: "Generate EMR" });
  }

  if (
    ["undelivered_in_transit", "delivered", "picked", "undelivered", "return_in_transit"]?.includes(status) &&
    !orders?.order_details?.is_sub_order_returned
  ) {
    clinicActions.push({ key: "accept-returned", kind: "accept_returned", label: "Accept Returned Items" });
  }

  if (status === "confirmed") {
    clinicActions.push({ key: "pack", kind: "pack", label: "Pack" });
  }

  if (status === "rx_pending" && prescriptionDoneValue === "required") {
    clinicActions.push({ key: "generate-emr-clinic", kind: "generate_emr", label: "Generate EMR" });
  }

  const actionList = module === "CENTRAL" ? centralActions : clinicActions;
  const primaryAction = actionList[0];
  const secondaryActions = actionList.slice(1);
  const showStopAssignmentAction = Boolean(orders?.order_details?.is_force_stop);

  if (showStopAssignmentAction) {
    secondaryActions.push({ key: "stop-assignment", kind: "stop_assignment", label: "Stop Assignment" });
  }

  const renderActionControl = (action: any, variant: "primary" | "menu", active = false) => {
    const buttonClass = variant === "primary" ? primaryActionBtnClass : menuActionBtnClass(active);

    switch (action?.kind) {
      case "qc_modal":
        if (variant === "primary") {
          return (
            <QCActionModal
              buttonName={action.label}
              buttonId={action.buttonId}
              orders={orders}
              buttonClassName={buttonClass}
            />
          );
        }
        return (
          <button type="button" onClick={() => setMenuQcModalAction(action.buttonId)} className={buttonClass}>
            {action.label}
          </button>
        );
      case "initiate_return":
        if (variant === "primary") {
          return <InitiateReturnProcess buttonName={action.label} orders={orders} buttonClassName={buttonClass} />;
        }
        return (
          <button type="button" onClick={() => setIsMenuInitiateReturnOpen(true)} className={buttonClass}>
            {action.label}
          </button>
        );
      case "cancel_rider":
        return (
          <button type="button" onClick={() => setIsCancelRiderModalOpen(true)} className={buttonClass}>
            {action.label}
          </button>
        );
      case "copy_payment_link":
        if (variant === "primary") {
          return (
            <div className="relative">
              <button
                type="button"
                onClick={handleCopyPaymentLink}
                disabled={isFetchingPaymentLink}
                className={`${buttonClass} disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {isFetchingPaymentLink ? "Fetching..." : action.label}
              </button>
              {copied && (
                <div className="absolute left-1/2 -translate-x-1/2 mt-2 rounded bg-black px-2 py-1 text-xs text-white">
                  Copied!
                </div>
              )}
            </div>
          );
        }
        return (
          <button
            type="button"
            onClick={handleCopyPaymentLink}
            disabled={isFetchingPaymentLink}
            className={`${buttonClass} disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {isFetchingPaymentLink ? "Fetching..." : action.label}
          </button>
        );
      case "pack":
        return (
          <button type="button" onClick={() => setOpen(true)} className={buttonClass}>
            {action.label}
          </button>
        );
      case "accept_returned":
        return (
          <button type="button" onClick={() => setAcceptReturnItem(true)} className={buttonClass}>
            {action.label}
          </button>
        );
      case "generate_emr": {
        const generateEmrButtonClass =
          variant === "primary"
            ? "w-[112px] rounded-lg bg-black px-1 py-2 text-xs font-medium text-white transition-opacity hover:opacity-90"
            : buttonClass;
        return (
          <button
            type="button"
            onClick={handleGenerateEmr}
            disabled={isGenerateEmrInProgress}
            className={`${generateEmrButtonClass} disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {isGenerateEmrInProgress ? "Generating..." : action.label}
          </button>
        );
      }
      case "stop_assignment":
        return (
          <button type="button" onClick={() => setIsStopAssignmentModalOpen(true)} className={buttonClass}>
            {action.label}
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <>
    <RBACFlag actionId="ORDER_MANAGEMENT_ACTION">
      {primaryAction && (
        <div className="flex items-start gap-2">
          <div className="min-w-[120px] flex-1">{renderActionControl(primaryAction, "primary")}</div>
          {secondaryActions?.length > 0 && (
            <Menu as="div" className="relative">
              {({ open: isMenuOpen }) => (
                <div className={`relative h-8 w-8 flex-shrink-0 ${isMenuOpen ? "z-[18]" : "z-10"}`}>
                  <Menu.Button className="flex h-full w-full items-center justify-center rounded-md bg-[#F1F1F1] transition-colors hover:bg-gray-200">
                    <FiMoreVertical className="h-5 w-5 text-gray-600" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-10 top-0 z-[20] w-52 origin-top-right rounded-lg border border-gray-100 bg-white shadow-lg focus:outline-none">
                      <div className="py-1">
                        {secondaryActions?.map((action) => (
                          <Menu.Item key={action.key}>
                            {({ active }) => renderActionControl(action, "menu", active)}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </div>
              )}
            </Menu>
          )}
        </div>
      )}

      {open && <PackButtonComponent open={open} setOpen={setOpen} orders={orders} />}
      {isSendPaymentLinkModalOpen && (
        <HDCartCheckoutModal
          open={isSendPaymentLinkModalOpen}
          setOpen={setIsSendPaymentLinkModalOpen}
          orderResponse={{ payment_link_details: { payment_link_url: orders?.order_details?.payment_link } }}
          cod={false}
          orderPlacedOpen={sendPaymentLinkOrderPlacedOpen}
          setOrderPlacedOpen={setSendPaymentLinkOrderPlacedOpen}
          phoneNumber={orders?.client_details?.mobile}
        />
      )}
      {acceptReturnItem && (
        <AcceptReturnItemButtonComponent open={acceptReturnItem} setOpen={setAcceptReturnItem} orders={orders} />
      )}
      {menuQcModalAction && (
        <QCActionModal
          buttonName=""
          buttonId={menuQcModalAction}
          orders={orders}
          hideTrigger
          controlledOpen={Boolean(menuQcModalAction)}
          onControlledOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setMenuQcModalAction(null);
            }
          }}
        />
      )}
      {isMenuInitiateReturnOpen && (
        <InitiateReturnProcess
          buttonName=""
          orders={orders}
          hideTrigger
          controlledOpen={isMenuInitiateReturnOpen}
          onControlledOpenChange={setIsMenuInitiateReturnOpen}
        />
      )}
      {isRxEmrPreviewVisible && (
        <RxEMRPreviewModal
          open={isRxEmrPreviewVisible}
          setOpen={setIsRxEmrPreviewVisible}
          emrData={rxEmrPreviewData}
          orderId={orders?.order_details?.order_id}
          userRxKey={orders?.order_details?.user_rx_key || orders?.user_rx_key}
          onStatusUpdated={async () => {
            await refreshOrderListAndStats();
          }}
        />
      )}

      <Transition appear show={isCancelRiderModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            if (!isCancelRiderInProgress) {
              setIsCancelRiderModalOpen(false);
            }
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white p-5 text-left shadow-xl transition-all">
                  <Dialog.Title className="text-sm font-semibold text-gray-900">Cancel Rider Request</Dialog.Title>
                  <p className="mt-2 text-sm text-gray-600">
                    Are you sure you want to cancel the rider request for this order?
                  </p>
                  <div className="mt-5 flex justify-end gap-2">
                    <button
                      type="button"
                      className="rounded-lg bg-[#F1F1F1] px-4 py-2 text-xs font-medium text-[#111827] transition-colors hover:bg-[#E5E7EB]"
                      onClick={() => setIsCancelRiderModalOpen(false)}
                      disabled={isCancelRiderInProgress}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      className="rounded-lg bg-black px-4 py-2 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={handleConfirmCancelRiderRequest}
                      disabled={isCancelRiderInProgress}
                    >
                      {isCancelRiderInProgress ? "Cancelling..." : "Yes, Cancel"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={isStopAssignmentModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            if (!isStopAssignmentInProgress) {
              setIsStopAssignmentModalOpen(false);
            }
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white p-5 text-left shadow-xl transition-all">
                  <Dialog.Title className="text-sm font-semibold text-gray-900">Stop Rider Assignment</Dialog.Title>
                  <p className="mt-2 text-sm text-gray-600">Are you sure you want to stop rider assignment process?</p>
                  <div className="mt-5 flex justify-end gap-2">
                    <button
                      type="button"
                      className="rounded-lg bg-[#F1F1F1] px-4 py-2 text-xs font-medium text-[#111827] transition-colors hover:bg-[#E5E7EB]"
                      onClick={() => setIsStopAssignmentModalOpen(false)}
                      disabled={isStopAssignmentInProgress}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      className="rounded-lg bg-black px-4 py-2 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={handleConfirmStopAssignment}
                      disabled={isStopAssignmentInProgress}
                    >
                      {isStopAssignmentInProgress ? "Stopping..." : "Yes, Stop"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      </RBACFlag>
      {status === "payment_due" && orders?.order_details?.is_central_order === true && (
        <RBACFlag actionId="SEND_PAYMENT_LINK_ACTION">
          <div className={`mt-2 flex flex-col gap-2 ${secondaryActions?.length > 0 ? "pr-10" : ""}`}>
            <button
              type="button"
              onClick={handleSendPaymentLink}
              className="w-full rounded-lg bg-black px-1 py-2 text-center text-xs font-medium text-white transition-opacity hover:opacity-90"
            >
              Send Payment Link
            </button>
            {orders?.order_details?.payment_link && (
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={handleCopyPaymentLink}
                  disabled={isFetchingPaymentLink}
                  className="w-full rounded-lg bg-black px-1 py-2 text-center text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isFetchingPaymentLink ? "Fetching..." : "Fetch & Copy Link"}
                </button>
                {copied && (
                  <div className="absolute left-1/2 -translate-x-1/2 mt-2 rounded bg-black px-2 py-1 text-xs text-white">
                    Copied!
                  </div>
                )}
              </div>
            )}
          </div>
        </RBACFlag>
      )}
    </>
  );
};

export default OrderActionButton;
